import React, { useMemo, useState, useCallback, useEffect } from "react";
import { ChevronDown, ChevronRight, Sparkles, Zap } from "lucide-react";
import Report20DataLoadingOverlay from "./Report20DataLoadingOverlay";

import CustomSelect from "../ui/CustomSelect";
function toYMD(value) {
  if (!value) return null;

  // Already YYYY-MM-DD
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  // IMPORTANT: avoid UTC shift
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isRevRepKey(actionKey) {
  const a = String(actionKey || "").trim().toLowerCase();
  return a === "revenue replacement" || a === "rev rep" || a === "revrep" || a.includes("revenue replacement");
}

function isPendingKey(actionKey) {
  const a = String(actionKey || "").trim().toLowerCase();
  return a === "pending disconnect" || a === "pending disconnects";
}

function isCleanKey(actionKey) {
  return String(actionKey || "").trim().toLowerCase() === "disconnect";
}

function formatUsd(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatUsdCompact(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return formatUsd(n);
}

// function formatDisplayYmd(ymd) {
//   if (!ymd || typeof ymd !== "string") return "—";
//   const d = new Date(`${ymd}T12:00:00`);
//   if (Number.isNaN(d.getTime())) return ymd;
//   return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
// }

function formatDisplayYmd(ymd) {
  const v = toYMD(ymd);
  if (!v) return "—";

  const [y, m, d] = v.split("-").map(Number);

  const date = new Date(Date.UTC(y, m - 1, d));

  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function KpiCard({ title, value, subtitle, accent, valueClass = "text-[var(--ink,#1c1c1a)]", onClick, clickable }) {
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={clickable ? onClick : undefined}
      className={`relative text-left w-full rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3 transition-shadow ${
        clickable ? "hover:bg-[var(--sunken,#eeece7)] cursor-pointer" : "cursor-default"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">{title}</p>
      <p className={`mt-1 text-[22px] font-semibold tabular-nums leading-tight ${valueClass}`}>{value}</p>
      {subtitle ? <p className="mt-1 text-[12px] leading-snug text-[var(--muted,#6b6a66)]">{subtitle}</p> : null}
    </button>
  );
}

function actionPillClass(label) {
  const x = String(label || "").toLowerCase();
  if (x.includes("revenue") || x.includes("rev rep")) return "bg-amber-50 text-amber-900 ring-1 ring-amber-200/80";
  if (x.includes("pending disconnect")) return "bg-teal-50 text-teal-900 ring-1 ring-teal-200/80";
  if (x.includes("disconnect")) return "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/80";
  if (x.includes("disconnect")) return "bg-rose-50 text-rose-800 ring-1 ring-rose-200/80";
  if (x.includes("follow")) return "bg-orange-50 text-orange-900 ring-1 ring-orange-200/80";
  if (x.includes("replace")) return "bg-sky-50 text-sky-900 ring-1 ring-sky-200/80";
  if (x.includes("hold")) return "bg-slate-100 text-slate-800 ring-1 ring-slate-200/80";
  return "bg-slate-50 text-slate-800 ring-1 ring-slate-200/60";
}

function NoticeCell({ urgency, daysToEnd }) {
  if (daysToEnd == null || !Number.isFinite(daysToEnd)) {
    return <span className="text-slate-400 text-sm">—</span>;
  }
  if (daysToEnd < 0) {
    return <span className="text-slate-500 text-sm">Ended</span>;
  }
  if (urgency === "critical") {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-rose-700">
        <Zap className="w-3.5 h-3.5 shrink-0" aria-hidden />
        {daysToEnd === 0 ? "TODAY" : `${daysToEnd} days`}
      </span>
    );
  }
  if (urgency === "warning") {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-700">
        <Zap className="w-3.5 h-3.5 shrink-0 opacity-70" aria-hidden />
        {daysToEnd} days
      </span>
    );
  }
  return <span className="text-sm text-emerald-700 font-medium">&gt; 30 days</span>;
}

function RevRepCell({ display }) {
  if (display === "na") {
    return <span className="text-sm text-slate-400">N/A</span>;
  }
  if (display === "eligible") {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-900 ring-1 ring-amber-200/80">
        + Rev Rep
      </span>
    );
  }
  return (
    <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600 ring-1 ring-slate-200/80">
      Ineligible
    </span>
  );
}

function rowsToCsv(rows) {
  const headers = [
    "Site",
    "Provider",
    "Circuit ID",
    "Category",
    "Service Type",
    "MRC",
    "End of Term",
    "Months Left",
    "ETF Est",
    "Action",
    "Days to End",
    "Rev Rep Eligible",
  ];
  const lines = [headers.join(",")];
  for (const r of rows) {
    const cells = [
      `"${String(r.siteName).replace(/"/g, '""')}"`,
      `"${String(r.provider).replace(/"/g, '""')}"`,
      `"${String(r.circuitId).replace(/"/g, '""')}"`,
      `"${String(r.category).replace(/"/g, '""')}"`,
      `"${String(r.serviceType).replace(/"/g, '""')}"`,
      r.mrc,
      r.endOfTerm ?? "",
      r.monthsLeft,
      Number(r.etf).toFixed(2),
      `"${String(r.actionLabel).replace(/"/g, '""')}"`,
      r.daysToEnd ?? "",
      r.revRepEligible ? "Yes" : "No",
    ];
    lines.push(cells.join(","));
  }
  return lines.join("\r\n");
}

/**
 * @param {Object} props
 * @param {Object} props.report - API `data` from GET /analytics/closed-site-financials
 * @param {boolean} [props.isLoading] - true while fetching (including month change with stale data shown)
 */
const Report20ClosedSiteFinancialsView = ({ report, isLoading = false }) => {
  const baseRows = report?.rows ?? [];
  const kpis = report?.kpis ?? {};
  const asOfDate = report?.asOfDate ?? "";
  const reportYear = report?.year;
  const reportMonth = report?.month;

  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const [noticeFilter, setNoticeFilter] = useState("all");
  const [circuitFilter, setCircuitFilter] = useState("all");
  const [sortEtfDesc, setSortEtfDesc] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const providers = useMemo(() => {
    const set = new Set();
    for (const r of baseRows) {
      if (r.provider && r.provider !== "—") set.add(r.provider);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [baseRows]);

  const resetDrillFilters = useCallback(() => {
    setCircuitFilter("all");
    setNoticeFilter("all");
    setSortEtfDesc(false);
  }, []);

  const filteredRows = useMemo(() => {
    let list = baseRows;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const blob = [r.siteName, r.provider, r.circuitId, r.billingAccount, r.serviceType, r.category]
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      });
    }
    if (providerFilter !== "all") {
      list = list.filter((r) => r.provider === providerFilter);
    }
    if (noticeFilter === "critical") {
      list = list.filter((r) => r.noticeUrgency === "critical");
    } else if (noticeFilter === "warning") {
      list = list.filter((r) => r.noticeUrgency === "warning");
    } else if (noticeFilter === "safe") {
      list = list.filter((r) => r.noticeUrgency === "safe");
    }

    if (circuitFilter === "revRep") {
      list = list.filter((r) => isRevRepKey(r.actionKey));
    } else if (circuitFilter === "pending") {
      list = list.filter((r) => isPendingKey(r.actionKey));
    } else if (circuitFilter === "disconnect") {
      list = list.filter((r) => isCleanKey(r.actionKey));
    }

    if (sortEtfDesc) {
      list = [...list].sort((a, b) => b.etf - a.etf || b.mrc - a.mrc);
    }

    return list;
  }, [baseRows, search, providerFilter, noticeFilter, circuitFilter, sortEtfDesc]);

  const distinctSitesFiltered = useMemo(() => new Set(filteredRows.map((r) => r.siteKey)).size, [filteredRows]);
  const distinctSitesTotal = useMemo(() => new Set(baseRows.map((r) => r.siteKey)).size, [baseRows]);

  const insightText = useMemo(() => {
    if (!baseRows.length) {
      // return "No closed-site circuits in this snapshot month, or snapshot data is not available for the selected period.";
      return "No closed-site circuits found in the current inventory."
    }
    const sites = kpis.closedSitesBilling ?? 0;
    const waste = formatUsd(kpis.monthlyWaste);
    const crit = baseRows.filter((r) => r.noticeUrgency === "critical").length;
    const topEtf = [...baseRows].sort((a, b) => b.etf - a.etf)[0];
    const tail =
      topEtf && topEtf.etf > 0
        ? ` Highest ETF exposure in view: ${topEtf.provider} ${topEtf.circuitId} (${formatUsd(topEtf.etf)}).`
        : "";
    return `You have ${sites} closed site${sites === 1 ? "" : "s"} still billing, representing ${waste}/mo in recoverable spend.${
      crit ? ` ${crit} circuit${crit === 1 ? "" : "s"} ${crit === 1 ? "has" : "have"} a critical notice window (7 days or less).` : ""
    }${tail}`;
  }, [baseRows, kpis.closedSitesBilling, kpis.monthlyWaste]);

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const exportCsv = () => {
    const csv = rowsToCsv(filteredRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // a.download = `closed-site-financials-${reportYear}-${String(reportMonth).padStart(2, "0")}.csv`;
    a.download = `closed-site-financials-report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rowKey = (r) => `${r.siteKey}|${r.circuitId}|${r.inventoryId ?? ""}`;

  const asOfLabel = formatDisplayYmd(asOfDate);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.ceil(filteredRows.length / pageSize);
  
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, providerFilter, noticeFilter, circuitFilter, sortEtfDesc]);

  return (
    <div className="relative min-h-[min(60vh,480px)]">
      <Report20DataLoadingOverlay show={isLoading} />
      <div
        className={`flex flex-col ${isLoading ? "pointer-events-none opacity-45 select-none" : ""}`}
        aria-hidden={isLoading}
      >
      {report?.snapshot?.date != null || report?.snapshot?.sourceRecordCount != null ? (
        // <div className="rounded-xl border border-slate-100 bg-white px-4 py-3 text-xs text-slate-600">
        //   Snapshot source:{" "}
        //   <span className="font-semibold text-slate-800">
        //     {report.snapshot?.date != null ? String(report.snapshot.date) : "—"}
        //   </span>
        //   {report.snapshot?.type ? ` (${report.snapshot.type})` : ""}
        //   {report.snapshot?.sourceRecordCount != null ? (
        //     <span className="text-slate-500"> · {report.snapshot.sourceRecordCount} records in file</span>
        //   ) : null}
        // </div>
        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3 text-[12px] text-[var(--muted,#6b6a66)]">
  Data source:{" "}
  <span className="font-semibold text-slate-800">
    {report.snapshot?.type === "database" ? "Live Inventory" : report.snapshot?.type || "—"}
  </span>
  {report.snapshot?.sourceRecordCount != null ? (
    <span className="text-slate-500">
      {" "}
      · {report.snapshot.sourceRecordCount} records
    </span>
  ) : null}
</div>
      ) : null}

      {/* <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Closed Sites — Active Circuits</h2>
          <p className="text-sm text-slate-500 mt-1">
            {kpis.distinctClosedSitesWithRows ?? 0} sites · {kpis.circuitCount ?? 0} circuits 
            as of {asOfLabel}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-slate-600">
            Total MRC at closed sites:{" "}
            <span className="font-bold text-rose-700">{formatUsd(kpis.monthlyWaste)}</span>
          </p>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!filteredRows.length}
            className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:pointer-events-none"
          >
            Export
          </button>
        </div>
      </div> */}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <KpiCard
          title="Closed Sites Billing"
          value={kpis.closedSitesBilling ?? 0}
          subtitle={`${kpis.circuitCount ?? 0} circuits in view`}
          accent="border-t-slate-600"
          valueClass="text-[var(--ink,#1c1c1a)]"
          clickable
          onClick={resetDrillFilters}
        />
        <KpiCard
          title="Monthly Waste"
          value={formatUsdCompact(kpis.monthlyWaste)}
          subtitle="MRC still billing (closed sites)"
          accent="border-t-slate-600"
          valueClass="text-[var(--ink,#1c1c1a)]"
          clickable
          onClick={resetDrillFilters}
        />
        <KpiCard
          title="Estimated ETF Exposure"
          value={formatUsdCompact(kpis.etfExposure)}
          subtitle="MRC × months remaining (future terms)"
          accent="border-t-slate-600"
          valueClass="text-[var(--ink,#1c1c1a)]"
          clickable
          onClick={() => {
            resetDrillFilters();
            setSortEtfDesc(true);
          }}
        />
        <KpiCard
          title="Revenue Replacement"
          value={kpis.revenueReplacementCount ?? 0}
          subtitle={formatUsd(kpis.revenueReplacementMrc) + " MRC"}
          accent="border-t-slate-600"
          valueClass="text-[var(--ink,#1c1c1a)]"
          clickable
          onClick={() => {
            resetDrillFilters();
            setCircuitFilter("revRep");
          }}
        />
        <KpiCard
          title="Disconnects"
          value={formatUsdCompact(kpis.cleanDisconnectMrc)}
          subtitle="No ETF (term ended or blank)"
          accent="border-t-slate-600"
          valueClass="text-[var(--ink,#1c1c1a)]"
          clickable
          onClick={() => {
            resetDrillFilters();
            setCircuitFilter("disconnect");
          }}
        />
        <KpiCard
          title="Pending Disconnect"
          value={kpis.pendingDisconnectCount ?? 0}
          subtitle={formatUsd(kpis.pendingDisconnectMrc) + "/mo in-flight"}
          accent="border-t-slate-600"
          valueClass="text-[var(--ink,#1c1c1a)]"
          clickable
          onClick={() => {
            resetDrillFilters();
            setCircuitFilter("pending");
          }}
        />
      </div>

      {(kpis.revenueReplacementCount ?? 0) > 0 ? (
        <div className="rounded-[10px] border mt-3 border-[var(--line,#e4e2dc)] bg-white px-4 py-3 flex gap-3 items-start">
          <Zap className="w-5 h-5 text-[var(--accent,#a51c1c)] shrink-0 mt-0.5" aria-hidden />
          <p className="text-[13px] text-[var(--ink,#1c1c1a)] leading-relaxed">
            <span className="font-semibold">Revenue replacement: </span>
            {kpis.revenueReplacementCount} circuit{kpis.revenueReplacementCount === 1 ? "" : "s"} use the Revenue Replacement
            action. Eligibility for ETF waiver is modeled as <strong>≥ 12 months billed</strong> (from contract term back from end
            date, or inventory create date when term is missing). Circuits marked <strong>+ Rev Rep</strong> meet the months-billed
            rule and still have a future term.
          </p>
        </div>
      ) : null}

      <div className="rounded-[10px] border mt-3 border-[var(--line,#e4e2dc)] bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <Sparkles className="w-5 h-5 text-[var(--accent,#a51c1c)] shrink-0 sm:mt-0" aria-hidden />
        <p className="text-[13px] text-[var(--ink,#1c1c1a)] leading-relaxed flex-1">
          <span className="font-semibold text-[var(--ink,#1c1c1a)]">Insight: </span>
          {insightText}
        </p>
        
      </div>

      <div className="flex flex-col mt-5 gap-3 lg:flex-row lg:flex-wrap lg:items-end rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">Search</label>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Circuit ID, billing account, address…"
            className="h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] px-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]"
          />
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">Circuit filter</label>
          <CustomSelect
            value={circuitFilter}
            onChange={(e) => setCircuitFilter(e.target.value)}
            className="h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] px-2.5 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
          >
            <option value="all">All circuits</option>
            <option value="revRep">Revenue replacement</option>
            <option value="pending">Pending disconnect</option>
            <option value="disconnect">Disconnect</option>
          </CustomSelect>
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">Provider</label>
          <CustomSelect
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] px-2.5 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
          >
            <option value="all">All providers</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </CustomSelect>
        </div>
        <div className="min-w-[180px]">
          <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">Notice window</label>
          <CustomSelect
            value={noticeFilter}
            onChange={(e) => setNoticeFilter(e.target.value)}
            className="h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] px-2.5 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
          >
            <option value="all">All</option>
            <option value="critical">Critical (≤ 7 days)</option>
            <option value="warning">Warning (8–30 days)</option>
            <option value="safe">Safe (&gt; 30 days)</option>
          </CustomSelect>
        </div>
      </div>

      {/* <p className="text-xs text-slate-500">
        Showing {distinctSitesFiltered} of {distinctSitesTotal} sites · {filteredRows.length} circuits
        {sortEtfDesc ? " · sorted by ETF (high → low)" : ""}
      </p> */}
      <div className="flex items-center mt-5 mb-3 justify-end flex-wrap gap-3 text-[13px] text-[var(--muted,#6b6a66)]">
  <div className="flex items-center gap-4">
    <p className="text-[13px] text-[var(--muted,#6b6a66)]">
      Total MRC at closed sites:{" "}
      <span className="font-semibold text-[var(--accent,#a51c1c)]">
        {formatUsd(kpis.monthlyWaste)}
      </span>
    </p>

    <button
      type="button"
      onClick={exportCsv}
      disabled={!filteredRows.length}
      className="inline-flex h-[38px] items-center gap-2 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] shadow-sm hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:pointer-events-none"
    >
      Export
    </button>
  </div>

</div>

      {/* <div className="rounded-2xl border border-gray-100 bg-white shadow-md overflow-hidden"> */}
      <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead>
              <tr className="bg-[var(--sunken2,#fbfaf8)] border-b border-[var(--line,#e4e2dc)] text-left text-[11px] font-semibold text-[var(--muted,#6b6a66)] uppercase tracking-[0.04em]">
                <th className="w-10 px-2 py-3" aria-label="Expand" />
                <th className="px-3 py-3">Provider</th>
                <th className="px-3 py-3">Circuit ID</th>
                <th className="px-3 py-3 min-w-[180px]">Address / Site</th>
                <th className="px-3 py-3">Service</th>
                <th className="px-3 py-3">End of Term</th>
                <th className="px-3 py-3">Billing Acct</th>
                <th className="px-3 py-3 text-right">MRC</th>
                <th className="px-3 py-3">Months Left</th>
                <th className="px-3 py-3 text-right">ETF Est.</th>
                <th className="px-3 py-3">Rev Rep?</th>
                <th className="px-3 py-3">Action</th>
                <th className="px-3 py-3">Notice</th>
              </tr>
            </thead>
            <tbody>
              {!baseRows.length ? (
                <tr>
                  {/* <td colSpan={13} className="px-4 py-12 text-center text-slate-500">
                    No closed-site snapshot rows for this month. Pick a month with an inventory snapshot in blob storage, or confirm
                    sites are marked <strong>Closed</strong> and circuits are not cancelled.
                  </td> */}
                  <td colSpan={13} className="px-4 py-12 text-center text-slate-500">No closed-site circuits found in the current inventory. 
                  Ensure sites are marked as "Closed" and circuits are not cancelled.</td>
                </tr>
              ) : !filteredRows.length ? (
                <tr>
                  <td colSpan={13} className="px-4 py-10 text-center text-slate-500">
                    No rows match the current filters.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((r) => {
                  const key = rowKey(r);
                  const open = expanded.has(key);
                  // const endDisp = r.endOfTerm || "—";
                  const endDisp = formatDisplayYmd(r.endOfTerm);
                  // const hasFutureTerm = r.endOfTerm && asOfDate && r.endOfTerm > asOfDate;
                  const end = toYMD(r.endOfTerm);
                  const asOf = toYMD(asOfDate);

                  const toTime = (ymd) => {
                    const [y, m, d] = ymd.split("-").map(Number);
                    return Date.UTC(y, m - 1, d);
                  };
                  
                  const hasFutureTerm = end && asOf && toTime(end) > toTime(asOf);
                  return (
                    <React.Fragment key={key}>
                      <tr className="border-b border-slate-100 hover:bg-slate-50/80">
                        <td className="px-2 py-2 align-top">
                          <button
                            type="button"
                            className="p-1 rounded text-slate-500 hover:bg-slate-200/60"
                            aria-expanded={open}
                            onClick={() => toggleExpand(key)}
                          >
                            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-700">{r.provider}</td>

<td className="px-3 py-2 text-sm text-slate-700 font-mono">
  {r.circuitId}
</td>

<td className="px-3 py-2 text-sm text-slate-700">
  {r.siteName}
</td>

<td className="px-3 py-2 text-sm text-slate-700">
  {r.category !== "—" ? r.category : r.serviceType}
</td>

<td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap">
  {endDisp}
</td>

<td className="px-3 py-2 text-sm text-slate-700 font-mono">
  {r.billingAccount}
</td>

<td className="px-3 py-2 text-sm text-slate-700 text-right">
  {formatUsd(r.mrc)}
</td>

<td className="px-3 py-2 text-sm text-slate-700">
  <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">
    {r.monthsLeft} mo
  </span>
</td>

<td className="px-3 py-2 text-sm text-slate-700 text-right">
  {formatUsd(r.etf)}
</td>

<td className="px-3 py-2 text-sm text-slate-700">
  <RevRepCell display={r.revRepDisplay} />
</td>

<td className="px-3 py-2 text-sm text-slate-700">
  <span className="inline-flex px-2 py-0.5 rounded-md text-xs bg-slate-100 text-slate-700">
    {r.actionLabel || "-"}
  </span>
</td>

<td className="px-3 py-2 text-sm text-slate-700">
  <NoticeCell urgency={r.noticeUrgency} daysToEnd={r.daysToEnd} />
</td>
                      </tr>
                      {open ? (
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                          <td colSpan={13} className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                <p className="text-xs font-bold text-slate-500 uppercase mb-2">Circuit details</p>
                                <dl className="space-y-1 text-sm text-slate-700">
                                  <div>
                                    <dt className="text-slate-500 inline">Technology: </dt>
                                    <dd className="inline">{r.serviceType}</dd>
                                  </div>
                                  <div>
                                    <dt className="text-slate-500 inline">Bandwidth: </dt>
                                    <dd className="inline">{r.bandwidth ?? "—"}</dd>
                                  </div>
                                  <div>
                                    <dt className="text-slate-500 inline">Inventory since: </dt>
                                    <dd className="inline">
                                      {/* {r.inventoryCreatedAt
                                        ? new Date(r.inventoryCreatedAt).toLocaleDateString("en-US")
                                        : "—"} */}
                                        {r.inventoryCreatedAt
                                        ? formatDisplayYmd(r.inventoryCreatedAt)
                                        : "—"}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-slate-500 inline">Months billed (est.): </dt>
                                    <dd className="inline">{r.monthsBilled != null ? `${r.monthsBilled} mo` : "—"}</dd>
                                  </div>
                                  <div>
                                    <dt className="text-slate-500 inline">Site closed: </dt>
                                    <dd className="inline">{r.siteClosedDate ? formatDisplayYmd(r.siteClosedDate) : "—"}</dd>
                                  </div>
                                </dl>
                              </div>
                              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                <p className="text-xs font-bold text-slate-500 uppercase mb-2">Early termination fee</p>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                  {hasFutureTerm ? (
                                    <>
                                      Contract ends {endDisp} — <strong>{r.monthsLeft}</strong> month
                                      {r.monthsLeft === 1 ? "" : "s"} remaining.{" "}
                                      <strong>
                                        {r.monthsLeft} × {formatUsd(r.mrc)} = {formatUsd(r.etf)}
                                      </strong>{" "}
                                      estimated ETF if cancelled without replacement.
                                    </>
                                  ) : (
                                    <>No future term date (or term ended). ETF treated as <strong>$0</strong>.</>
                                  )}
                                </p>
                              </div>
                              <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4 shadow-sm">
                                <p className="text-xs font-bold text-violet-800 uppercase mb-2">Revenue replacement</p>
                                <p className="text-sm text-slate-800 leading-relaxed">
                                  {r.revRepEligible && r.revRepDisplay !== "na" ? (
                                    <>
                                      Qualifies under the <strong>≥ 12 months billed</strong> rule. Replacing with a new circuit at
                                      another location may waive ETF per carrier policy — confirm with your playbook.
                                    </>
                                  ) : r.revRepDisplay === "na" ? (
                                    <>Not applicable (no remaining term / no ETF scenario).</>
                                  ) : (
                                    <>Does not yet meet the 12-month billed threshold (or term data is incomplete).</>
                                  )}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg bg-slate-50 px-4 py-3">
  <div className="flex items-center justify-between text-sm text-slate-600 flex-wrap ">

  {/* LEFT: Page size */}
  <div className="flex items-center gap-2">
    <span>Show:</span>
    <CustomSelect
  value={pageSize}
  onChange={(e) => {
    setPageSize(Number(e.target.value)); // ✅ THIS is required
    setCurrentPage(1); // reset to first page
  }}
  className="border rounded px-2 py-1"
>
  <option value={10}>10</option>
  <option value={20}>20</option>
  <option value={25}>25</option>
  <option value={50}>50</option>
</CustomSelect>
    <span>per page</span>
  </div>

  {/* CENTER: Pagination */}
  <div className="flex items-center gap-1">
    <button
      onClick={() => setCurrentPage(1)}
      disabled={currentPage === 1}
      className="px-2 py-1 border rounded disabled:opacity-40"
    >
      «
    </button>

    <button
      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
      disabled={currentPage === 1}
      className="px-2 py-1 border rounded disabled:opacity-40"
    >
      Prev
    </button>

    {Array.from({ length: totalPages }, (_, i) => i + 1)
      .slice(
        Math.max(currentPage - 3, 0),
        Math.min(currentPage + 2, totalPages)
      )
      .map((page) => (
        <button
          key={page}
          onClick={() => setCurrentPage(page)}
          className={`px-3 py-1 rounded border text-sm transition-colors duration-150 ${
            currentPage === page
              ? "bg-[var(--accent,#a51c1c)] text-white border-[var(--accent,#a51c1c)] hover:opacity-90"
              : "bg-white border-slate-200 hover:bg-slate-100"
          }`}
        >
          {page}
        </button>
      ))}

    <button
      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
      disabled={currentPage === totalPages || totalPages === 0}
      className="px-2 py-1 border rounded disabled:opacity-40"
    >
      Next
    </button>

    <button
      onClick={() => setCurrentPage(totalPages)}
      disabled={currentPage === totalPages || totalPages === 0}
      className="px-2 py-1 border rounded disabled:opacity-40"
    >
      »
    </button>
  </div>

  {/* RIGHT: Showing count */}
  <div>
    Showing {(currentPage - 1) * pageSize + 1} –{" "}
    {Math.min(currentPage * pageSize, filteredRows.length)} of{" "}
    {filteredRows.length} records
  </div>

  </div>
  </div>
      </div>
    </div>
  );
};

export default Report20ClosedSiteFinancialsView;
